/**
 * Public Intake API — unauthenticated calls to backend
 */

/** Same-origin /api in dev (Vite proxy); absolute URL in production */
function getIntakeApiUrl(path: string): string {
  const envUrl = import.meta.env.VITE_SIGNALING_SERVER_URL?.replace(/\/$/, '') ?? '';
  if (!envUrl) {
    return `/api${path}`;
  }
  return `${envUrl}/api${path}`;
}

async function publicFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(getIntakeApiUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Request failed');
  }
  return data as T;
}

export interface ContractorPublicInfo {
  ok: boolean;
  contractor: {
    name: string;
    schedulingAvailable: boolean;
  };
}

export interface IntakeSubmitResponse {
  ok: boolean;
  submissionId: string;
  contractorName: string;
  schedulingAvailable: boolean;
}

export interface IntakeSlot {
  start: string;
  label: string;
}

export interface IntakeSlotsResponse {
  ok: boolean;
  slots: IntakeSlot[];
  timezone: string;
}

export const intakeApi = {
  getContractor(contractorId: string) {
    return publicFetch<ContractorPublicInfo>(`/public/contractor/${contractorId}`);
  },

  submitForm(params: {
    contractorId: string;
    firstName: string;
    lastName?: string;
    phoneNumber: string;
    email?: string;
    project?: string;
  }) {
    return publicFetch<IntakeSubmitResponse>('/public/intake/submit', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  getSlots(submissionId: string) {
    return publicFetch<IntakeSlotsResponse>(`/public/intake/${submissionId}/slots`);
  },

  scheduleCall(submissionId: string, scheduledTime: string, message?: string) {
    return publicFetch<{ ok: boolean; joinLink: string }>(
      `/public/intake/${submissionId}/schedule`,
      {
        method: 'POST',
        body: JSON.stringify({ scheduledTime, message }),
      }
    );
  },

  startRecording(submissionId: string) {
    return publicFetch<{ ok: boolean; roomId: string }>(
      `/public/intake/${submissionId}/recording`,
      { method: 'POST' }
    );
  },
};

export function getIntakeUrl(contractorId: string): string {
  const base =
    import.meta.env.VITE_PUBLIC_APP_URL ||
    import.meta.env.VITE_NETWORK_URL ||
    (typeof window !== 'undefined' ? window.location.origin : 'https://www.wlkthru.co');
  return `${base.replace(/\/$/, '')}/r/${contractorId}`;
}
